import { formatDistanceToNowStrict } from 'date-fns';
import { CircleCheck, Cloud, CloudAlert, CloudOff, Lock, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSync } from './SyncProvider';
import type { SyncStatus } from './engine';

export function describeSync(status: SyncStatus): { label: string; detail: string } {
  const pending = status.pending ? `${status.pending} ${status.pending === 1 ? 'change' : 'changes'} waiting` : '';
  switch (status.phase) {
    case 'syncing':
      return { label: 'Syncing…', detail: pending };
    case 'offline':
      return { label: 'Offline', detail: pending || 'Everything is saved on this device.' };
    case 'read-only':
      return { label: 'Read-only', detail: 'Your plan has lapsed, so changes aren’t saved to your account.' };
    case 'error':
      return { label: 'Sync problem', detail: status.error ?? 'Will retry automatically.' };
    default:
      if (status.pending) return { label: 'Changes waiting', detail: pending };
      return {
        label: 'Synced',
        detail: status.lastSyncedAt ? `${formatDistanceToNowStrict(new Date(status.lastSyncedAt))} ago` : ''
      };
  }
}

function Icon({ status }: { status: SyncStatus }) {
  const className = 'h-4 w-4';
  if (status.phase === 'syncing') return <RefreshCw className={`${className} animate-spin`} />;
  if (status.phase === 'offline') return <CloudOff className={className} />;
  if (status.phase === 'read-only') return <Lock className={className} />;
  if (status.phase === 'error') return <CloudAlert className={`${className} text-clinical-warning`} />;
  if (status.pending) return <Cloud className={className} />;
  return <CircleCheck className={`${className} text-clinical-primary`} />;
}

/** Compact sync indicator for page headers. Links to Account & plan. Hidden in demo mode. */
export function SyncBadge() {
  const sync = useSync();
  if (!sync) return null;
  const { label } = describeSync(sync.status);
  return (
    <Link
      to="/more/account"
      title={label}
      aria-label={`Sync: ${label}`}
      className="focus-ring flex items-center gap-1.5 rounded-full border border-clinical-line bg-clinical-card px-2.5 py-1 text-xs font-medium text-clinical-muted"
    >
      <Icon status={sync.status} />
      <span className="hidden sm:inline">{label}</span>
    </Link>
  );
}
